/**
 * Read-only helpers for consumers (renderer, HUD, AI, audio). Pure; they never mutate the state.
 */
import { POWER, RULES, STAMINA, SpecialId, goalLineFor, yardLineLabel, yardsToGoal } from '@hf/shared';
import { BallState, ConversionChoice, Phase, PlayCall, SpecialPhase, isKickCall } from './enums';
import type { PlayerSim, SimState } from './state';

export const offenseIndex = (s: SimState): 0 | 1 => (s.rules.offense === 0 ? 0 : 1);
export const defenseIndex = (s: SimState): 0 | 1 => (s.rules.offense === 0 ? 1 : 0);
export const offensePlayer = (s: SimState): PlayerSim => s.players[offenseIndex(s)];
export const defensePlayer = (s: SimState): PlayerSim => s.players[defenseIndex(s)];

/** Index of the ball carrier, or -1 if the ball is not held. */
export const carrierIndex = (s: SimState): number => (s.ball.state === BallState.HELD ? s.ball.holder : -1);

/** The play is live (after the snap, before the whistle). */
export const isLive = (s: SimState): boolean =>
  s.rules.phase === Phase.RUSH_COUNT || s.rules.phase === Phase.LIVE;

/**
 * Is player `p` allowed to cross the LOS right now?
 *  - PRE_SNAP: nobody (the offense is clamped behind it; a defender crossing is OFFSIDE).
 *  - RUSH_COUNT: the offense yes; the defender only once the count ended early (offense crossed / Blitz) —
 *    crossing before that is OFFSIDE.
 *  - otherwise: yes.
 */
export const mayCrossLos = (s: SimState, p: number): boolean => {
  const r = s.rules;
  if (r.phase === Phase.PRE_SNAP) return false;
  if (r.phase === Phase.RUSH_COUNT) return p === r.offense || r.offenseCrossed;
  return true;
};

/** Signed distance of x past the LOS toward the defense's side (positive = beyond the LOS for the offense). */
export const pastLos = (s: SimState, x: number): number => (x - s.rules.los) * s.rules.attackDir;

/**
 * `pastLos(s, x) > margin` as a boolean. Hot paths (every tick) use these predicates: a double returned from a call
 * the optimizer does not inline is boxed into a fresh HeapNumber; a boolean never is.
 */
export const beyondLos = (s: SimState, x: number, margin = 0): boolean =>
  (x - s.rules.los) * s.rules.attackDir > margin;

/** Player `i`'s head centre is beyond the LOS (no double crosses the call: allocation-free on hot paths). */
export const headBeyondLos = (s: SimState, i: number): boolean =>
  (s.players[i === 0 ? 0 : 1].x - s.rules.los) * s.rules.attackDir > 0;

/** The ball is beyond the LOS (allocation-free, see `headBeyondLos`). */
export const ballBeyondLos = (s: SimState): boolean => (s.ball.x - s.rules.los) * s.rules.attackDir > 0;

/**
 * QB mode (ARCHITECTURE §5 Controls): the offense player holds the ball, no forward pass was thrown yet this play,
 * and the ball carrier is behind the LOS. In QB mode Action = charge/throw (▼ while charging = pump fake), there is
 * no stiff-arm; ▼ hold = truck, ▼+Action chord = spin, ▲ = jump/hurdle still apply.
 */
export const isQbMode = (s: SimState, p: number): boolean =>
  isLive(s) &&
  s.ball.state === BallState.HELD &&
  s.ball.holder === p &&
  p === s.rules.offense &&
  !s.rules.passThrown &&
  !headBeyondLos(s, p);

/**
 * Classic kickoff not yet kicked (line-up or after the kicker's snap): the kicker stays behind the LOS, the
 * receiver behind the restraining line (KICK.restrainYards), and the kicker's ▼ is his onside call at the release.
 */
export const kickoffPending = (s: SimState): boolean =>
  s.rules.playCall === PlayCall.KICKOFF &&
  !s.rules.passThrown &&
  (s.rules.phase === Phase.PRE_SNAP || s.rules.phase === Phase.LIVE);

/**
 * Classic kick return: the player who may TAKE A KNEE right now (▼ held `KICK.kneelHoldFrames` → touchback), or -1.
 * That is the receiver holding the kicked ball in his own end zone (the one the kicker attacks) during live play —
 * the same end-zone test as the rules' kneel (head or ball across the goal line), so the HUD prompt never lies.
 */
export const kneelablePlayer = (s: SimState): number => {
  const r = s.rules;
  const b = s.ball;
  if (r.phase !== Phase.LIVE || !isKickCall(r.playCall) || !r.passThrown) return -1;
  if (b.state !== BallState.HELD) return -1;
  const recv = 1 - r.offense;
  if (b.holder !== recv) return -1;
  const goal = goalLineFor(r.attackDir);
  const pl = s.players[recv === 0 ? 0 : 1];
  return (b.x - goal) * r.attackDir >= 0 || (pl.x - goal) * r.attackDir >= 0 ? recv : -1;
};

/** Yards to the attacked goal line from the LOS. */
export const losYardsToGoal = (s: SimState): number => yardsToGoal(s.rules.los, s.rules.attackDir);

/** The stamina gate (STAMINA.minFeintCost): below it, the player's next move MUST BE REAL. */
export const mustBeReal = (p: PlayerSim): boolean => p.stamina < STAMINA.minFeintCost;

export interface HudSpecial {
  /** SpecialId (NONE when specials are off). */
  id: number;
  /**
   * SpecialPhase as the HUD may show it: a secretly ARMED Blitz is reported as IDLE (hidden information).
   */
  phase: number;
  /** Seconds left on an ACTIVE timed special (Bulldozer), 0 otherwise (e.g. an armed Hail Mary). */
  timerSec: number;
  /** Power is full and the special can be triggered now or later this play (icon lit). */
  ready: boolean;
}

export interface HudLastResult {
  /** PlayResultKind of the last finished play (NONE before the first). */
  kind: number;
  yards: number;
  points: number;
  scorer: number;
  firstDown: boolean;
}

/** Snapshot of everything the broadcast HUD shows. Allocates; call at most once per rendered frame. */
export interface HudSnapshot {
  phase: Phase;
  score: [number, number];
  offense: number;
  down: number;
  distance: number;
  goalToGo: boolean;
  /** Broadcast yard label of the LOS (0–50) and which side of midfield it is on, from the offense's view. */
  losLabel: number;
  losOwnSide: boolean;
  /** Broadcast yard label of the first-down line (0–50). */
  firstDownLabel: number;
  playClockSec: number;
  rushCountSec: number;
  /** PlayCall (NORMAL / FG_TOSS / PAT_TOSS) — "FG" tag on the chyron after the snap. */
  playCall: number;
  stamina: [number, number];
  power: [number, number];
  /** "MUST BE REAL" flags (stamina below the cheapest feint). */
  mustBeReal: [boolean, boolean];
  special: [HudSpecial, HudSpecial];
  /** CharacterIndex of each player. */
  characters: [number, number];
  /** Drives started by each player ("DRIVE 2 OF 4") and drives left. */
  driveNumber: [number, number];
  drivesPerPlayer: number;
  drivesLeft: [number, number];
  /** Conversion choice pending (TD scored): show "UP = 2-PT · ACTION = 1-PT TOSS" with a countdown. */
  conversionPending: boolean;
  conversionSec: number;
  overtime: boolean;
  otRound: number;
  /** Classic format only (0 / 1 in Drives). */
  gameClockSec: number;
  half: number;
  /**
   * MatchFormat (DRIVES / CLASSIC): which clock / drive counters to show. Always set by `hudSnapshot` (optional only
   * so hand-built HUD fixtures stay valid).
   */
  format?: number;
  /** Classic: the game clock is running (it keeps running between plays after a tackle in bounds). Always set. */
  clockRunning?: boolean;
  /**
   * Classic kick return: the returner who may take a knee now ("HOLD ▼ = TAKE A KNEE"), -1 otherwise
   * (`kneelablePlayer`). Always set by `hudSnapshot` (optional only so hand-built HUD fixtures stay valid).
   */
  kneel?: number;
  /**
   * A conversion try is being played (after the choice): 'pat' = the 1-point toss, 'two_point' = the 2-point play,
   * null otherwise. The HUD shows "EXTRA POINT" / "2-PT TRY" instead of a down & distance (a try is one play).
   * Always set by `hudSnapshot` (optional only so hand-built HUD fixtures stay valid).
   */
  tryPlay?: 'pat' | 'two_point' | null;
  lastResult: HudLastResult;
  winner: number;
}

const hudSpecial = (s: SimState, i: 0 | 1): HudSpecial => {
  const p = s.players[i];
  const sp = p.special;
  const phase = sp.phase === SpecialPhase.ARMED ? SpecialPhase.IDLE : sp.phase;
  const active = sp.phase === SpecialPhase.ACTIVE;
  return {
    id: sp.id,
    phase,
    timerSec: active ? Math.ceil(sp.timer / 60) : 0,
    ready: sp.id !== SpecialId.NONE && phase === SpecialPhase.IDLE && p.power >= POWER.max,
  };
};

export const hudSnapshot = (s: SimState): HudSnapshot => {
  const r = s.rules;
  const toGoal = yardsToGoal(r.los, r.attackDir);
  const left = (i: number): number => Math.max(0, r.drivesPerPlayer - (r.drivesStarted[i] ?? 0));
  const p0 = s.players[0];
  const p1 = s.players[1];
  const lr = r.lastResult;
  const conversionPending = r.phase === Phase.CONVERSION && r.conversion === ConversionChoice.PENDING;
  return {
    phase: r.phase,
    score: [r.score[0] ?? 0, r.score[1] ?? 0],
    offense: r.offense,
    down: r.down,
    distance: Math.max(1, Math.round(r.distance)),
    goalToGo: r.goalToGo,
    losLabel: yardLineLabel(r.los),
    losOwnSide: toGoal > 50,
    firstDownLabel: yardLineLabel(r.firstDownX),
    playClockSec: Math.ceil(r.playClock / 60),
    rushCountSec: Math.ceil(r.rushCount / 60),
    playCall: r.playCall,
    stamina: [p0.stamina, p1.stamina],
    power: [p0.power, p1.power],
    mustBeReal: [mustBeReal(p0), mustBeReal(p1)],
    special: [hudSpecial(s, 0), hudSpecial(s, 1)],
    characters: [p0.character, p1.character],
    driveNumber: [r.drivesStarted[0] ?? 0, r.drivesStarted[1] ?? 0],
    drivesPerPlayer: r.drivesPerPlayer,
    drivesLeft: [left(0), left(1)],
    conversionPending,
    conversionSec: conversionPending
      ? Math.max(0, Math.ceil((RULES.conversionChoiceFrames - r.phaseFrame) / 60))
      : 0,
    overtime: r.overtime,
    otRound: r.otRound,
    gameClockSec: Math.ceil(r.gameClock / 60),
    half: r.half,
    format: r.format,
    clockRunning: r.clockRunning,
    kneel: kneelablePlayer(s),
    tryPlay: r.conversionPlay ? (r.conversion === ConversionChoice.TWO_POINT ? 'two_point' : 'pat') : null,
    lastResult: {
      kind: lr.kind,
      yards: Math.round(lr.yards),
      points: lr.points,
      scorer: lr.scorer,
      firstDown: lr.firstDown,
    },
    winner: r.winner,
  };
};

/**
 * Field-by-field equality for HUD republishing (meters compared at whole-number resolution). Lives next to the
 * interface: whoever adds a HudSnapshot field adds it here, and match-scene never needs to change.
 */
export const hudEquals = (a: HudSnapshot, b: HudSnapshot): boolean => {
  const m = (x: number): number => Math.round(x);
  const sp = (x: HudSpecial, y: HudSpecial): boolean =>
    x.id === y.id && x.phase === y.phase && x.timerSec === y.timerSec && x.ready === y.ready;
  const lr = (x: HudLastResult, y: HudLastResult): boolean =>
    x.kind === y.kind &&
    x.yards === y.yards &&
    x.points === y.points &&
    x.scorer === y.scorer &&
    x.firstDown === y.firstDown;
  for (let i = 0; i < 2; i++) {
    if (
      a.score[i] !== b.score[i] ||
      m(a.stamina[i]!) !== m(b.stamina[i]!) ||
      m(a.power[i]!) !== m(b.power[i]!) ||
      a.mustBeReal[i] !== b.mustBeReal[i] ||
      !sp(a.special[i]!, b.special[i]!) ||
      a.characters[i] !== b.characters[i] ||
      a.driveNumber[i] !== b.driveNumber[i] ||
      a.drivesLeft[i] !== b.drivesLeft[i]
    )
      return false;
  }
  return (
    a.phase === b.phase &&
    a.offense === b.offense &&
    a.down === b.down &&
    a.distance === b.distance &&
    a.goalToGo === b.goalToGo &&
    a.losLabel === b.losLabel &&
    a.losOwnSide === b.losOwnSide &&
    a.firstDownLabel === b.firstDownLabel &&
    a.playClockSec === b.playClockSec &&
    a.rushCountSec === b.rushCountSec &&
    a.playCall === b.playCall &&
    a.drivesPerPlayer === b.drivesPerPlayer &&
    a.conversionPending === b.conversionPending &&
    a.conversionSec === b.conversionSec &&
    a.overtime === b.overtime &&
    a.otRound === b.otRound &&
    a.gameClockSec === b.gameClockSec &&
    a.half === b.half &&
    a.format === b.format &&
    a.clockRunning === b.clockRunning &&
    a.kneel === b.kneel &&
    (a.tryPlay ?? null) === (b.tryPlay ?? null) &&
    lr(a.lastResult, b.lastResult) &&
    a.winner === b.winner
  );
};
